import React from "react";
import { useTranslation } from "react-i18next";
import { getTransportationTypeName } from "../../utils/vehicleDisplay";
import {
  Box,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  useDisclosure,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  useBreakpointValue,
} from "@chakra-ui/react";

function BookingDetailsModal({ tour, accommodationUnits, accommodation }) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { t } = useTranslation("vehicle");

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      timeZone: "Europe/Sarajevo",
      weekday: "short",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // Responsive table layout based on screen size
  const tableVariant = useBreakpointValue({
    base: "simple",
    md: "striped",
  });

  const TourTable = ({ tour }) => {
    return (
      <Box p={4} maxW="1200px" mx="auto">
        <Text fontSize="2xl" fontWeight="bold" mb={4}>
          Tour Details
        </Text>

        <Table variant={tableVariant} mb={8}>
          <Thead>
            <Tr>
              <Th colSpan={2}>
                <Text fontSize="xl" fontWeight="semibold">
                  {tour?.name}
                </Text>
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            <Tr>
              <Td fontWeight="bold">Cities:</Td>
              <Td>{tour?.cities.join(", ")}</Td>
            </Tr>
            <Tr>
              <Td fontWeight="bold">Pickup Location:</Td>
              <Td>{tour?.pickupLocation}</Td>
            </Tr>
            <Tr>
              <Td fontWeight="bold">Selected Vehicle:</Td>
              <Td>
                {getTransportationTypeName(tour?.selectedVehicleCategory, t)} - EUR
                {tour?.selectedVehicleCategory.price} (
                {tour?.selectedVehicleCategory.seats} seats)
              </Td>
            </Tr>
          </Tbody>
        </Table>

        <Text fontSize="sm" fontWeight="bold" mb={1}>
          Accommodation Units
        </Text>
        <Table variant={tableVariant} size="sm">
          <Thead>
            <Tr>
              <Th>Name</Th>
              <Th>Quantity</Th>
              <Th>Total Price</Th>
              <Th>Start Date</Th>
              <Th>End Date</Th>
              <Th>Accommodation</Th>
            </Tr>
          </Thead>
          <Tbody>
            {tour?.accommodationUnits?.map((unit, index) => (
              <Tr key={index}>
                <Td>{unit?.name}</Td>
                <Td>{unit?.quantity}</Td>
                <Td>{unit?.totalPrice}</Td>
                <Td>{formatDate(unit?.startDate)}</Td>
                <Td>{formatDate(unit?.endDate)}</Td>
                <Td>
                  {unit?.stay.name} ({unit?.stay.type})<br />
                  {unit?.stay.address}, {unit?.stay.city}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    );
  };

  const AccommodationsTable = ({ units, accommodation }) => {
    return (
      <Box p={4} maxW="1200px" mx="auto">
        <Text fontSize="2xl" fontWeight="bold" mb={4}>
          Accommodation: {accommodation?.name}
        </Text>

        <Text fontSize="sm" fontWeight="bold" mb={1}>
          Accommodation Units
        </Text>
        <Table variant={tableVariant} size="sm">
          <Thead>
            <Tr>
              <Th>Name</Th>
              <Th>Quantity</Th>
              <Th>Total Price</Th>
              <Th>Start Date</Th>
              <Th>End Date</Th>
            </Tr>
          </Thead>
          <Tbody>
            {units?.map((unit, index) => (
              <Tr key={index}>
                <Td>{unit?.name}</Td>
                <Td>{unit?.quantity}</Td>
                <Td>{unit?.totalPrice}</Td>
                <Td>{formatDate(unit?.startDate)}</Td>
                <Td>{formatDate(unit?.endDate)}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    );
  };

  return (
    <>
      <Button onClick={onOpen}>Click to view booking details</Button>

      <Modal size={"full"} isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalBody>
            <Box overflowX={"auto"} flexWrap={"wrap"}>
              {tour ? (
                <TourTable tour={tour} />
              ) : (
                <AccommodationsTable
                  units={accommodationUnits}
                  accommodation={accommodation}
                />
              )}
            </Box>
          </ModalBody>

          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={onClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

export default BookingDetailsModal;
